import React from 'react';
import { useTranslation } from 'react-i18next';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator,
  SelectTrigger, SelectValue,
} from '../ui/select';

// Radix Select does not allow an empty value, so "inherit" has a sentinel.
export const INHERIT = '__inherit__';

/** Reason code from GET /bots/models -> translation key. */
export const reasonKey = (code) => `bots.unavailable_${code || 'other'}`;

export const modelLabel = (models, id) => models?.find((m) => m.id === id)?.label || id;

// Models of providers that are switched off (or that BusyBee has no key for)
// are not offered at all. Models above the plan stay visible but disabled,
// so users see what an upgrade unlocks.
const HIDDEN_REASONS = new Set(['provider_disabled', 'not_configured']);

const TIER_STYLE = {
  cheap: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  standard: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
  premium: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
};

/** A model's tier as one pill of one width, so tiers line up in every list. */
export const TierBadge = ({ tier, className }) => {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex min-w-[5.5rem] justify-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
      TIER_STYLE[tier] || 'bg-muted text-muted-foreground', className)}>
      {t(`bots.tier_${tier}`)}
    </span>
  );
};

/** One model in the picker: the name (also what the closed picker shows), then why it is off and its tier. */
const ModelItem = ({ model, t }) => (
  <SelectPrimitive.Item value={model.id} disabled={!model.available}
    className="relative flex w-full cursor-default select-none items-center gap-3 rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50">
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator><Check className="h-4 w-4" /></SelectPrimitive.ItemIndicator>
    </span>
    <span className="flex-1 min-w-0 truncate"><SelectPrimitive.ItemText>{model.label}</SelectPrimitive.ItemText></span>
    {!model.available && <span className="text-xs text-muted-foreground whitespace-nowrap">{t(reasonKey(model.unavailable_reason))}</span>}
    <TierBadge tier={model.tier} />
  </SelectPrimitive.Item>
);

/**
 * Model picker grouped by provider.
 *
 * value: model id, or null for "inherit" (only when inheritLabel is given).
 * heading: a line above the choices saying what the choice is for (a chat's own model).
 */
const ModelSelect = ({ models = [], value, onChange, inheritLabel, heading, disabled, id, className }) => {
  const { t } = useTranslation();
  const groups = [];
  // Keep the current value visible even if its provider was switched off.
  for (const m of models.filter((x) => !HIDDEN_REASONS.has(x.unavailable_reason) || x.id === value)) {
    let g = groups.find((x) => x.provider === m.provider);
    if (!g) {
      g = { provider: m.provider, label: m.provider_label, items: [] };
      groups.push(g);
    }
    g.items.push(m);
  }

  return (
    <Select
      value={value ?? (inheritLabel ? INHERIT : undefined)}
      // Inside a <form> Radix keeps a hidden native <select>; the browser restoring the form
      // (reload, back) fires it with "", which no option has: ignore it, it is not a choice.
      onValueChange={(v) => { if (v) onChange(v === INHERIT ? null : v); }}
      disabled={disabled}
    >
      <SelectTrigger id={id} className={className}>
        <SelectValue placeholder={t('bots.field_model')} />
      </SelectTrigger>
      <SelectContent className="max-h-80 min-w-[18rem]">
        {heading && <SelectGroup><SelectLabel className="text-xs text-muted-foreground">{heading}</SelectLabel></SelectGroup>}
        {inheritLabel && (
          <>
            <SelectItem value={INHERIT}>{inheritLabel}</SelectItem>
            <SelectSeparator />
          </>
        )}
        {groups.map((g) => (
          <SelectGroup key={g.provider}>
            <SelectLabel className="text-xs text-muted-foreground">{g.label}</SelectLabel>
            {g.items.map((m) => <ModelItem key={m.id} model={m} t={t} />)}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
};

export default ModelSelect;
